import { useMemo } from "react";
import { Tooltip } from "antd";
import { TableColumnsType, TableColumnType } from "antd";
import {
  TableData,
  getSortableString,
  createFiltersForColumn,
} from "views/uikit/VLTable/tableUtils";
import TextWithTooltip from "views/components/TextWithTooltip";
import { ScanResult, Wafer } from "./types";
import PathCell from "./PathCell";

export interface ScanResultRow extends TableData {
  key: string;
  srName: string;
  srPath: string;
  date: string;
  lot: string;
  recipe: string;
  setup: string;
  wafers: number;
  defects: number;
  defectsAfterVerification: number;
}

function formatScanStartTime(value: string | null): string {
  if (!value) return "-";
  // Naive timestamps from DataServer - render them as given rather than shifting them
  // into the browser's timezone, so they match what MDC shows the same operator.
  return value.replace("T", " ").slice(0, 19);
}

function flattenScanResult(result: ScanResult): ScanResultRow {
  return {
    key: result.sr_path,
    srName: result.sr_name,
    srPath: result.sr_path,
    date: formatScanStartTime(result.date),
    lot: result.lot,
    recipe: result.recipe ?? "-",
    setup: result.setup,
    wafers: result.wafers,
    defects: result.defects,
    defectsAfterVerification: result.defects_after_verification,
  };
}

const numericSorter =
  (key: keyof ScanResultRow) => (a: ScanResultRow, b: ScanResultRow) =>
    Number(a[key]) - Number(b[key]);

const stringSorter =
  (key: keyof ScanResultRow) => (a: ScanResultRow, b: ScanResultRow) =>
    getSortableString(a[key]).localeCompare(getSortableString(b[key]));

export default function useScanResultsData(
  scanResults: ScanResult[],
  filterState: Record<string, string[]> = {},
  selectedWafers: Record<string, string[]> = {},
) {
  const dataSource = useMemo(
    () => scanResults.map(flattenScanResult),
    [scanResults],
  );

  // Wafer detail is kept beside the rows rather than on them: ScanResultRow extends
  // TableData, whose SupportedValue does not admit arrays of objects. The expanded row
  // and the export both look wafers up by sr_path.
  const wafersByPath: Record<string, Wafer[]> = useMemo(
    () =>
      Object.fromEntries(
        scanResults.map((result) => [result.sr_path, result.wafer_details]),
      ),
    [scanResults],
  );

  const columns: TableColumnsType<ScanResultRow> = useMemo(() => {
    // One-line clamp with a hover tooltip, the way Task Manager renders its cells - it
    // is what keeps line-height from varying cell to cell.
    const text = (value: string) => <TextWithTooltip text={value} />;

    const filterable = (
      key: keyof ScanResultRow,
      title: string,
      width?: string,
    ): TableColumnType<ScanResultRow> => ({
      key: key as string,
      title,
      dataIndex: key as string,
      width,
      sorter: stringSorter(key),
      filterSearch: true,
      filters: createFiltersForColumn(dataSource as TableData[], key as string),
      filteredValue: filterState[key as string] || null,
      render: text,
    });

    return [
      {
        // No width: it takes whatever the others and the expand / checkbox columns
        // leave, so the table always fits its container instead of scrolling sideways.
        ...filterable("srName", "SR Name"),
        // The short Job/Setup/Lot name is what reads well; the full path is what you
        // actually need to paste elsewhere, so PathCell carries both. This render must
        // stay after the spread - it deliberately replaces filterable's shared one,
        // since PathCell does its own ellipsis and tooltip.

        render: (_value: string, record: ScanResultRow) => (
          <PathCell label={record.srName} path={record.srPath} />
        ),
      },
      {
        key: "date",
        title: "Date",
        dataIndex: "date",
        width: "15%",
        defaultSortOrder: "descend",
        sorter: stringSorter("date"),
        render: text,
      },
      // Lot, not Job: SR Name is already the job name, and the lot is what tells two
      // scan results of the same job and setup apart.
      filterable("lot", "Lot", "13%"),
      {
        ...filterable("recipe", "Recipe", "18%"),
        sorter: stringSorter("recipe"),
      },
      filterable("setup", "Setup", "12%"),
      {
        key: "wafers",
        title: "Wafers",
        dataIndex: "wafers",
        width: "8%",
        align: "right",
        sorter: numericSorter("wafers"),
        // The row checkbox cannot show a partial selection - antd renders it checked or
        // not - so the count carries it. Without this, selecting 2 of 5 wafers and
        // selecting all 5 look identical from the collapsed row.
        render: (value: number, record: ScanResultRow) => {
          const selected = selectedWafers[record.srPath]?.length ?? 0;
          return selected > 0 && selected < value ? (
            <Tooltip title={`${selected} of ${value} wafers selected`}>
              <span>
                {selected} / {value}
              </span>
            </Tooltip>
          ) : (
            <span>{value}</span>
          );
        },
      },
      {
        key: "defects",
        title: "Defects",
        dataIndex: "defects",
        width: "9%",
        align: "right",
        sorter: numericSorter("defects"),
        render: (value: number, record: ScanResultRow) => (
          <Tooltip
            title={`${record.defectsAfterVerification.toLocaleString()} after verification`}
          >
            <span>{value.toLocaleString()}</span>
          </Tooltip>
        ),
      },
    ];
  }, [dataSource, filterState, selectedWafers]);

  return { columns, dataSource, wafersByPath };
}
